// File: src/components/AIPersonalizationCard.jsx
// Description: AI Personalization Card component for dashboard integration

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Chip,
  Button,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  AutoAwesome,
  Settings,
  Analytics,
  Visibility,
  TrendingUp,
  Psychology
} from '@mui/icons-material';
import { useAI } from '../context/AIContext';
import AIService from '../services/AIService';

const AIPersonalizationCard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    personalizationEnabled,
    personalizationAnalytics,
    togglePersonalization,
    updatePersonalizationAnalytics
  } = useAI();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load analytics on mount
  useEffect(() => {
    const loadAnalytics = async () => {
      if (personalizationEnabled) {
        setLoading(true);
        try {
          const analytics = await AIService.getPersonalizationAnalytics();
          updatePersonalizationAnalytics(analytics);
        } catch (error) {
          setError('Failed to load analytics');
        } finally {
          setLoading(false);
        }
      }
    };

    loadAnalytics();
  }, [personalizationEnabled, updatePersonalizationAnalytics]);

  const handleToggle = async () => {
    setLoading(true);
    try {
      await AIService.simulateAIProcessing(500);
      togglePersonalization();
      setError(null);
    } catch (error) {
      setError('Failed to toggle personalization');
    } finally {
      setLoading(false);
    }
  };

  const personalizationRate = personalizationAnalytics.totalViews > 0 
    ? (personalizationAnalytics.personalizedViews / personalizationAnalytics.totalViews) * 100 
    : 0;

  return (
    <Card sx={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 25px rgba(0, 0, 0, 0.1)',
      }
    }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              AI Personalization
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={personalizationEnabled}
                onChange={handleToggle}
                disabled={loading}
                color="primary"
              />
            }
            label=""
          />
        </Box>

        {/* Status */}
        <Box sx={{ mb: 2 }}>
          <Chip
            label={personalizationEnabled ? 'Active' : 'Inactive'}
            color={personalizationEnabled ? 'success' : 'default'}
            size="small"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {personalizationEnabled 
              ? 'Your profile adapts to each viewer automatically'
              : 'Enable AI personalization to customize your profile for different viewers'
            }
          </Typography>
        </Box>

        {/* Analytics */}
        {personalizationEnabled && !loading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Personalization Rate
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={personalizationRate}
                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40 }}>
                {Math.round(personalizationRate)}%
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  {personalizationAnalytics.totalViews}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Views
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                  {personalizationAnalytics.personalizedViews}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Personalized
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {personalizationEnabled ? 'Updating analytics...' : 'Enabling personalization...'}
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Analytics />}
            sx={{ fontSize: '0.75rem' }}
            disabled={!personalizationEnabled}
          >
            View Analytics
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Settings />}
            sx={{ fontSize: '0.75rem' }}
            disabled={!personalizationEnabled}
          >
            Configure Rules
          </Button>
        </Box>

        {/* Quick Stats */}
        {personalizationEnabled && personalizationAnalytics.topViewers?.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Top Viewers
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {personalizationAnalytics.topViewers.slice(0, 3).map((viewer, index) => (
                  <Chip
                    key={index}
                    label={`${viewer.name} (${viewer.views})`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AIPersonalizationCard;
